import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useState, useEffect, KeyboardEvent } from "react"; // Added useEffect
import Navbar from "../Header/Navbar";
import {
  FaEyeSlash,
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
  FaEnvelope,
} from "react-icons/fa";
import { IoEye } from "react-icons/io5";
import { toast } from "react-hot-toast";
import i18next from "i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ACCOUNT_END_POINT } from "src/config";
import useLocalStorage from "src/hooks/useLocalStorage";
import player from "src/img/player.png";
import { FaLock, FaUser } from "react-icons/fa6";
import { ArrowRight, Mail } from "lucide-react";
import { transformSerialTimestamp } from "src/hooks/transformSerialTimestamp";

export const RegisterPlayer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isSpecialRegistration = Boolean(
    (location.state as { specialRegistration?: boolean } | null)?.specialRegistration
  );
  const [apiError, setApiError] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(isSpecialRegistration ? 2 : 1);
  const params = useParams();
  const [uid] = useLocalStorage("UID", "");
  const [isRegistered, setIsRegistered] = useState(false);
  const [registeredUserData, setRegisteredUserData] = useState<any>(null);
  const navigate = useNavigate();

  // ✅ Schema including ageOption and isTutor
  const registerPlayerSchema = z
    .object({
      ageOption: z.enum(["adulto", "tutor"]),
      isTutor: z.boolean().default(false).optional(),
      firstName: z
        .string()
        .min(1, { message: t("register_form.errors.firstName.required") })
        .max(50),
      lastName: z
        .string()
        .min(1, { message: t("register_form.errors.lastName.required") })
        .max(50),
      workEmail: z
        .string()
        .email({ message: t("register_form.errors.workEmail.required") }),
      password: z
        .string()
        .min(8, { message: t("register_form.errors.password.minLength") })
        .regex(/[A-Z]/, {
          message: t("register_form.errors.password.password_uppercase"),
        })
        .regex(/[a-z]/, {
          message: t("register_form.errors.password.password_lowercase"),
        })
        .regex(/[0-9]/, {
          message: t("register_form.errors.password.password_number"),
        })
        .regex(/[^A-Za-z0-9]/, {
          message: t("register_form.errors.password.password_special"),
        }),
      confirmPassword: z.string().min(1, {
        message: t("register_form.errors.confirmPassword.required"),
      }),
      acceptedTerms: z.boolean().refine((val) => val === true, {
        message: t("register_form.errors.terms_required"),
      }),
      acceptedPrivacy: z.boolean().refine((val) => val === true, {
        message: t("register_form.errors.privacy_required"),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("register_form.errors.confirmPassword.match"),
      path: ["confirmPassword"],
    });

  type RegisterPlayerFormData = z.infer<typeof registerPlayerSchema>;

  const {
    register: registerPlayer,
    handleSubmit: handleSubmitPlayer,
    formState: { errors: errorsPlayer, isSubmitting: isSubmittingPlayer, isValid },
    reset,
    setError,
    trigger,
    getValues,
    watch,
    setValue,
  } = useForm<RegisterPlayerFormData>({
    resolver: zodResolver(registerPlayerSchema),
    mode: "onChange",
    defaultValues: {
      isTutor: false,
      acceptedTerms: false,
      acceptedPrivacy: false,
      ageOption: "adulto",
    },
  });

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");
  const ageOption = watch("ageOption") as string;
  const isTutor = watch("isTutor") as boolean;

  useEffect(() => {
    if (confirmPassword) {
      trigger("confirmPassword");
    }
  }, [password, confirmPassword, trigger]);

  // ✅ Sync isTutor with ageOption automatically
  useEffect(() => {
    setValue("isTutor", isSpecialRegistration ? false : ageOption === "tutor");
  }, [ageOption, isSpecialRegistration, setValue]);

  // Navigation across three steps:
  // 1 = age selection, 2 = personal info, 3 = security
  const handleNext = async () => {
    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      const isValid = await trigger(["firstName", "lastName", "workEmail"]);
      if (isValid) setCurrentStep(3);
      return;
    }
  };

  const handlePrev = () => {
    if (isSpecialRegistration && currentStep === 2) {
      navigate("/register-selection", { state: { showSeasonRoles: true } });
      return;
    }

    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const handleFormKeyDown = async (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Enter") return;
    if (currentStep === 3) return;

    event.preventDefault();
    await handleNext();
  };

  const accountType = params.type;
  const showTutorField = accountType === "2";

  const onSubmitPlayer = async (values: RegisterPlayerFormData) => {
    setApiError(null);
    const accessPos = Math.floor(Math.random() * 9) + 1;
    const timestampSec = Math.floor(Date.now() / 1000);
    const accessKey = transformSerialTimestamp(
      "WZ2308ES00007",
      accessPos,
      timestampSec
    );

    try {
      const formData = new FormData();
      formData.append("_method", "POST");
      formData.append("mail", values.workEmail);
      formData.append("password", values.password);
      formData.append("name", values.firstName);
      formData.append("surname", values.lastName);
      formData.append("language_code", i18next.language === "es" ? "ES" : "EN");
      formData.append("country_code", "ES");
      formData.append("account_type_id", "2");
      formData.append("is_tutor", isSpecialRegistration ? "0" : isTutor ? "1" : "0");
      formData.append("access_key", accessKey);
      formData.append("access_pos", accessPos.toString());
      formData.append("access_sn", "WZ2308ES00007");

      const response = await fetch(`${ACCOUNT_END_POINT}`, {
        method: "POST",
        body: formData,
      });

      const responseData = await response.json();

      if (responseData.status === "error" && responseData.result) {
        setApiError({
          code: responseData.result.error_id || "unknown",
          message:
            responseData.result.error_msg ||
            responseData.message ||
            "Registration failed",
        });
      } else if (responseData.status === "ok") {
        setRegisteredUserData({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.workEmail,
        });
        setIsRegistered(true);
      } else {
        throw new Error(responseData.message || "Registration failed");
      }

      reset();
    } catch (error) {
      setError("root", {
        type: "manual",
        message:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  };

  if (isRegistered) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800">
        <Navbar isRegister={true} />

        <div className="flex items-center justify-center min-h-[calc(100vh-112px)] p-6">
          <div className="w-full max-w-md bg-white/5 backdrop-blur-lg rounded-3xl overflow-hidden shadow-2xl border border-white/10">
            <div className="p-8 text-center">
              {/* Animated Checkmark */}
              <div className="relative mb-8">
                <div className="absolute inset-0 rounded-full animate-ping opacity-75"></div>
                <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto animate-in zoom-in duration-300">
                  <FaCheckCircle className="w-14 h-14 text-white" />
                </div>
              </div>

              {/* Success Content */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">
                  {t("register.success_message")}
                </h2>

                <p className="text-gray-300">
                  {t("register.success_description", {
                    firstName: registeredUserData?.firstName,
                  })}
                </p>

                <div className="mt-4 p-3 bg-indigo-900/30 rounded-lg border border-indigo-400/20">
                  <p className="text-indigo-100 flex items-center justify-center gap-2">
                    <Mail className="w-5 h-5" />
                    {t("register.check_email")}
                  </p>
                </div>

                <div className="mt-6">
                  <a
                    href="https://test.wizardfootball.com/login"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium rounded-xl transition-all duration-300 hover:shadow-lg"
                  >
                    {t("register.go_to_login")}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800">
      <Navbar isRegister={true} />

      <div className="flex items-center justify-center min-h-[calc(100vh-112px)] p-6">
        <div className="w-full max-w-5xl bg-white/5 backdrop-blur-lg rounded-3xl overflow-hidden shadow-2xl border border-white/10">
          <div className="flex flex-col md:flex-row">
            {/* Left Side - Visual */}
            <div className="md:w-2/5 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-8 flex flex-col items-center justify-center relative">
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative z-10 text-center space-y-6">
                <img
                  src={player}
                  alt="Player"
                  className="w-48 h-48 mx-auto object-contain drop-shadow-xl"
                />

                <div>
                  <h1 className="text-3xl font-bold text-white mb-2">
                    {t("register.welcome")}
                  </h1>
                  <p className="text-gray-200">
                    {t("register.subtitle")}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="md:w-3/5 p-8">
              <div className="flex items-center justify-between mb-8">
                {!isSpecialRegistration && (
                  <>
                    <div className="flex items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1
                          ? "bg-indigo-600 text-white"
                          : "bg-gray-700 text-gray-400"
                          }`}
                      >
                        {currentStep > 1 ? (
                          <FaCheckCircle className="w-4 h-4" />
                        ) : (
                          "1"
                        )}
                      </div>
                      <span
                        className={`ml-2 text-sm ${currentStep >= 1 ? "text-white" : "text-gray-400"
                          }`}
                      >
                        {t("register_form.fields.age_option")}
                      </span>
                    </div>

                    <div
                      className={`flex-1 h-1 mx-4 rounded ${currentStep >= 2 ? "bg-indigo-600" : "bg-gray-700"
                        }`}
                    ></div>
                  </>
                )}

                <div className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-700 text-gray-400"
                      }`}
                  >
                    {currentStep > 2 ? <FaCheckCircle className="w-4 h-4" /> : isSpecialRegistration ? "1" : "2"}
                  </div>
                  <span
                    className={`ml-2 text-sm ${currentStep >= 2 ? "text-white" : "text-gray-400"
                      }`}
                  >
                    {t("register_form.personal_info")}
                  </span>
                </div>

                <div
                  className={`flex-1 h-1 mx-4 rounded ${currentStep >= 3 ? "bg-indigo-600" : "bg-gray-700"
                    }`}
                ></div>

                <div className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-700 text-gray-400"
                      }`}
                  >
                    {isSpecialRegistration ? "2" : "3"}
                  </div>
                  <span
                    className={`ml-2 text-sm ${currentStep >= 3 ? "text-white" : "text-gray-400"
                      }`}
                  >
                    {t("register_form.security")}
                  </span>
                </div>
              </div>

              {/* Form Header */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-1">
                  {currentStep === 1
                    ? t("register.age_step_title")
                    : currentStep === 2
                      ? t("register.step1_title")
                      : t("register.step2_title")}
                </h2>
                <p className="text-gray-400">
                  {currentStep === 1
                    ? t("register.age_step_subtitle")
                    : currentStep === 2
                      ? t("register.step1_subtitle")
                      : t("register.step2_subtitle")}
                </p>
              </div>

              {/* Error Messages */}
              {errorsPlayer.root && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 flex items-start">
                  <svg
                    className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{errorsPlayer.root.message}</span>
                </div>
              )}

              {apiError && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 flex items-start">
                  <svg
                    className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    {apiError.message === "Mail is already used"
                      ? `${t("register_form.errors.api_error.email_used")}`
                      : apiError.message}
                  </span>
                </div>
              )}

              <form
                //@ts-ignore
                onSubmit={handleSubmitPlayer(onSubmitPlayer)}
                onKeyDown={handleFormKeyDown}
                className="space-y-6"
                noValidate
              >
                {currentStep === 1 && (
                  <div className="space-y-6 animate-in slide-in-from-right duration-300">
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-300">
                        {t("register_form.fields.age_option")}
                      </p>
                      <div className="flex flex-col md:flex-row gap-3">
                        <label className="flex items-center gap-2 bg-gray-800/50 border border-gray-700 rounded-xl p-3 cursor-pointer hover:bg-gray-700/50 transition">
                          <input
                            type="radio"
                            value="adulto"
                            {...registerPlayer("ageOption")}
                            defaultChecked
                            className="text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-gray-200">
                            {t("register_form.fields.adult_option")}
                          </span>
                        </label>

                        <label className="flex items-center gap-2 bg-gray-800/50 border border-gray-700 rounded-xl p-3 cursor-pointer hover:bg-gray-700/50 transition">
                          <input
                            type="radio"
                            value="tutor"
                            {...registerPlayer("ageOption")}
                            className="text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-gray-200">
                            {t("register_form.fields.tutor_option")}
                          </span>
                        </label>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleNext}
                      className="w-full mt-6 py-3 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl shadow transition-all duration-300 flex items-center justify-center"
                    >
                      {t("register_form.buttons.continue")}
                      <FaArrowRight className="ml-2 w-4 h-4" />
                    </button>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-6 animate-in slide-in-from-left duration-300">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label
                          htmlFor="firstName"
                          className="flex items-center text-sm font-medium text-gray-300"
                        >
                          <FaUser className="w-4 h-4 mr-2 text-indigo-400" />
                          {ageOption === "tutor"
                            ? t("register_form.placeholders.tutor_firstName")
                            : t("register_form.placeholders.player_firstName")}
                        </label>
                        <input
                          id="firstName"
                          type="text"
                          {...registerPlayer("firstName")}
                          className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white placeholder-gray-500"
                          placeholder={
                            ageOption === "tutor"
                              ? t("register_form.placeholders.tutor_firstName")
                              : t("register_form.placeholders.player_firstName")
                          }
                        />
                        {errorsPlayer.firstName && (
                          <p className="text-sm text-red-400">
                            {errorsPlayer.firstName.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label
                          htmlFor="lastName"
                          className="flex items-center text-sm font-medium text-gray-300"
                        >
                          <FaUser className="w-4 h-4 mr-2 text-indigo-400" />
                          {ageOption === "tutor"
                            ? t("register_form.placeholders.tutor_lastName")
                            : t("register_form.placeholders.player_lastName")}
                        </label>
                        <input
                          id="lastName"
                          type="text"
                          {...registerPlayer("lastName")}
                          className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white placeholder-gray-500"
                          placeholder={
                            ageOption === "tutor"
                              ? t("register_form.placeholders.tutor_lastName")
                              : t("register_form.placeholders.player_lastName")
                          }
                        />
                        {errorsPlayer.lastName && (
                          <p className="text-sm text-red-400">
                            {errorsPlayer.lastName.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Email Field */}
                    <div className="space-y-2">
                      <label
                        htmlFor="workEmail"
                        className="flex items-center text-sm font-medium text-gray-300"
                      >
                        <FaEnvelope className="w-4 h-4 mr-2 text-indigo-400" />
                        {t("register_form.fields.workEmail")}
                      </label>
                      <input
                        id="workEmail"
                        type="email"
                        {...registerPlayer("workEmail")}
                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white placeholder-gray-500"
                        placeholder={t("register_form.placeholders.workEmail")}
                      />
                      {errorsPlayer.workEmail && (
                        <p className="text-sm text-red-400">
                          {errorsPlayer.workEmail.message}
                        </p>
                      )}
                    </div>

                    {/* Navigation for step 2 */}
                    <div className="flex gap-4 pt-4">
                      <button
                        type="button"
                        onClick={handlePrev}
                        className="flex-1 py-3 px-6 border border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-xl font-medium transition-colors duration-300 flex items-center justify-center"
                      >
                        <FaArrowLeft className="mr-2 w-4 h-4" />
                        {t("register_form.buttons.back")}
                      </button>

                      <button
                        type="button"
                        onClick={handleNext}
                        className="flex-1 py-3 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl shadow transition-all duration-300 flex items-center justify-center"
                      >
                        {t("register_form.buttons.continue")}
                        <FaArrowRight className="ml-2 w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-6 animate-in slide-in-from-left duration-300">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Password */}
                      <div className="space-y-2">
                        <label
                          htmlFor="password"
                          className="flex items-center text-sm font-medium text-gray-300"
                        >
                          <FaLock className="w-4 h-4 mr-2 text-indigo-400" />
                          {t("register_form.fields.password")}
                        </label>
                        <div className="relative">
                          <input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            {...registerPlayer("password")}
                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white placeholder-gray-500 pr-10"
                            autoComplete="new-password"
                            placeholder={t(
                              "register_form.placeholders.password"
                            )}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                            tabIndex={-1}
                          >
                            {showPassword ? (
                              <IoEye className="w-5 h-5" />
                            ) : (
                              <FaEyeSlash className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        {errorsPlayer.password && (
                          <p className="text-sm text-red-400">
                            {errorsPlayer.password.message}
                          </p>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div className="space-y-2">
                        <label
                          htmlFor="confirmPassword"
                          className="flex items-center text-sm font-medium text-gray-300"
                        >
                          <FaLock className="w-4 h-4 mr-2 text-indigo-400" />
                          {t("register_form.fields.confirmPassword")}
                        </label>
                        <div className="relative">
                          <input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            {...registerPlayer("confirmPassword")}
                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white placeholder-gray-500 pr-10"
                            autoComplete="new-password"
                            placeholder={t(
                              "register_form.placeholders.confirmPassword"
                            )}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                            tabIndex={-1}
                          >
                            {showConfirmPassword ? (
                              <IoEye className="w-5 h-5" />
                            ) : (
                              <FaEyeSlash className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        {errorsPlayer.confirmPassword && (
                          <p className="text-sm text-red-400">
                            {errorsPlayer.confirmPassword.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Terms and Privacy */}
                    <div className="space-y-4 pt-2 border-t border-gray-700/50">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="acceptedTerms"
                            type="checkbox"
                            {...registerPlayer("acceptedTerms")}
                            className="h-5 w-5 rounded border-gray-600 bg-gray-700 text-indigo-600 focus:ring-indigo-500"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label
                            htmlFor="acceptedTerms"
                            className="text-gray-300"
                          >
                            {t("register_form.fields.accept_terms")} {" "}
                            <a
                              href="/legal#terms"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-400 hover:text-indigo-300 underline"
                            >
                              {t("register_form.fields.terms_link")}
                            </a>
                          </label>
                          {errorsPlayer.acceptedTerms && (
                            <p className="text-sm text-red-400">
                              {errorsPlayer.acceptedTerms.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="acceptedPrivacy"
                            type="checkbox"
                            {...registerPlayer("acceptedPrivacy")}
                            className="h-5 w-5 rounded border-gray-600 bg-gray-700 text-indigo-600 focus:ring-indigo-500"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label
                            htmlFor="acceptedPrivacy"
                            className="text-gray-300"
                          >
                            {t("register_form.fields.accept_privacy")} {" "}
                            <a
                              href="/legal#privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-400 hover:text-indigo-300 underline"
                            >
                              {t("register_form.fields.privacy_link")}
                            </a>
                          </label>
                          {errorsPlayer.acceptedPrivacy && (
                            <p className="text-sm text-red-400">
                              {errorsPlayer.acceptedPrivacy.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Form Navigation */}
                    <div className="flex gap-4 pt-4">
                      <button
                        type="button"
                        onClick={handlePrev}
                        className="flex-1 py-3 px-6 border border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-xl font-medium transition-colors duration-300 flex items-center justify-center"
                      >
                        <FaArrowLeft className="mr-2 w-4 h-4" />
                        {t("register_form.buttons.back")}
                      </button>

                      <button
                        type="submit"
                        disabled={!isValid || isSubmittingPlayer}
                        className="flex-1 py-3 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl shadow transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmittingPlayer ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            {t("register_form.buttons.submitting")}
                          </>
                        ) : (
                          <>
                            <FaCheckCircle className="mr-2 w-4 h-4" />
                            {t("register_form.buttons.submit")}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
